<script lang="ts">
import { IconDefinition } from '@icons/core';
import { faClock } from '@icons/regular/faClock';
import { faDroneFront } from '@icons/regular/faDroneFront';
import { faGamepadModern } from '@icons/regular/faGamepadModern';

interface RotatingIcon {
  key: string;
  icon: IconDefinition;
}

const ROTATING_ICON: RotatingIcon[] = [
  { key: 'a', icon: faClock },
  { key: 'b', icon: faDroneFront },
  { key: 'c', icon: faGamepadModern },
];

export default { name: 'MainSectionProficiency' };
</script>

<script setup lang="ts">
import ParallaxWrapper from '@components/ParallaxWrapper.vue';

import FeatureDetailsBlock from './FeatureDetailsBlock.vue';
import MainSectionPanel from './MainSectionPanel.vue';
</script>

<template>
  <section id="section-proficiency">
    <MainSectionPanel>
      <div :class="['w-100', 'd-flex', 'align-items-stretch']">
        <!-- #region Rotating Icons -->

        <div class="rotating-icons">
          <div class="icon-rotator">
            <div
              v-for="{ key, icon } in ROTATING_ICON"
              :key="key"
              :class="['rotating-icon', `icon-${key}`]"
            >
              <div class="icon-origin">
                <div class="icon-inner">
                  <Icon v-bind="{ icon }" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- #endregion Rotating Icons -->
      </div>

      <FeatureDetailsBlock
        title="Shorten Time to Proficiency"
        details="Fly without risking hardware, having to charge drone batteries, and worrying about weather. Users can train wherever and whenever they want."
        justification="right"
      />
    </MainSectionPanel>

    <ParallaxWrapper :factor="-0.05">
      <div class="shape-container">
        <div class="shape-a"></div>
        <ParallaxWrapper :factor="-0.05">
          <div class="shape-b"></div>
        </ParallaxWrapper>
        <ParallaxWrapper :factor="-0.08">
          <div class="shape-c"></div>
        </ParallaxWrapper>
      </div>
    </ParallaxWrapper>
  </section>
</template>

<style scoped lang="scss">
@keyframes rotateIcons {
  0% {
    transform: rotateZ(0);
  }

  100% {
    transform: rotateZ(360deg);
  }
}

$icon-size: 150px;
$icon-distance: 150px;
$icon-rotation-duration: 10s;

.rotating-icons {
  position: relative;
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;

  @include app-mobile {
    width: 100%;
    height: 200px;
    margin-bottom: 3rem;
    transform: scale(0.5);
  }

  &::before {
    $circle-size: $icon-distance + $icon-size;
    position: absolute;
    width: $circle-size;
    height: $circle-size;
    border: 6px dotted #fff3;
    border-radius: 100%;
    content: '';
  }
}

.icon-rotator {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0;
  height: 0;
  animation: rotateIcons $icon-rotation-duration;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

.rotating-icon {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0;
  height: 0;

  &.icon-a {
    $icon-a-x: $icon-distance * math.cos(0deg);
    $icon-a-y: $icon-distance * math.sin(0deg);

    transform: translate($icon-a-x, $icon-a-y);
    // transform: translate3d($icon-a-x, $icon-a-y, -100px);

    // @keyframes size {
    //   0% {
    //     transform: translate3d($icon-a-x, $icon-a-y, 0);
    //   }
    //
    //   50% {
    //     transform: translate3d($icon-a-x, $icon-a-y, -100px);
    //   }
    //
    //   100% {
    //     transform: translate3d($icon-a-x, $icon-a-y, 0);
    //   }
    // }
    //
    // animation: size $icon-rotation-duration;
    // animation-timing-function: linear;
    // animation-iteration-count: infinite;
  }

  &.icon-b {
    $icon-b-x: $icon-distance * math.cos(120deg);
    $icon-b-y: $icon-distance * math.sin(120deg);

    transform: translate($icon-b-x, $icon-b-y);
  }

  &.icon-c {
    $icon-c-x: $icon-distance * math.cos(-120deg);
    $icon-c-y: $icon-distance * math.sin(-120deg);

    transform: translate($icon-c-x, $icon-c-y);
  }
}

.icon-origin {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0;
  height: 0;
  perspective: 500px;
  animation: rotateIcons $icon-rotation-duration;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-direction: reverse;
}

.icon-inner {
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: $icon-size;
  height: $icon-size;
  color: white;
  font-size: 4em;
  border-radius: 100%;
  transform: translateZ(0px) rotateX(15deg) rotateY(0) rotateZ(340deg);

  .icon-a & {
    background: radial-gradient(
      farthest-corner at 90% 10%,
      #ff7c72,
      #ff3300,
      #c90047 90%
    );
  }

  .icon-b & {
    background: radial-gradient(
      farthest-corner at 90% 10%,
      #ffa65a,
      #ff6000,
      #ff5a5a 90%
    );
  }

  .icon-c & {
    background: radial-gradient(
      farthest-corner at 90% 10%,
      #fee471,
      #ff9a1b,
      #b9a900 90%
    );
  }

  .fa-icon {
    filter: drop-shadow(0px 0px 5px white);
  }
}

.container {
  z-index: 1;
}

.parallax-wrapper {
  position: relative;
}

.shape-container {
  position: absolute;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-110px) rotateZ(10deg);
}

.shape-a {
  position: absolute;
  width: 200vw;
  height: 384px;
  background: linear-gradient(#ffa900, #dc4f17);
  opacity: 1;
}

.shape-b {
  position: absolute;
  top: -250px;
  right: 650px;
  width: 434px;
  height: 138px;
  background: linear-gradient(
    -90deg,
    rgba(255, 58, 0, 0),
    30%,
    #ff5e00,
    #ff5e00
  );
  box-shadow: -20px 10px 40px rgb(0 3 53 / 9%);
  transform: skew(15deg);
}

.shape-c {
  position: absolute;
  bottom: -290px;
  left: 720px;
  width: 1045.63px;
  height: 196.59px;

  // width: 434px;
  background: linear-gradient(
    -90deg,
    rgba(255, 91, 0, 0),
    30%,
    #ff4f00,
    #ff8000
  );
  box-shadow: -20px 10px 40px rgb(0 3 53 / 9%);
  transform: rotateZ(180deg) skew(15deg);
}
</style>
